<template>
  <div>
    <div class="channels relative flex gap-size-5 text-size-6">
      <a
        v-for="channel in channels"
        :key="channel.enum"
        :href="channel.landingPage"
        class="channel flex items-center gap-[0.5em] border-b-4 border-b-transparent pb-[0.1em] font-bold text-muted hover:text-muted-hover"
        :style="activeChannel === channel.enum ? activeToggleStyle : null"
        :class="{
          'is-active !text-primary': channel.enum === activeChannel,
        }"
        @click="onClick($event, channel)"
      >
        <SpriteSymbol :name="channel.icon" class="size-[0.7em]" />
        <span>{{ channel.label }}</span>
      </a>
    </div>

    <LanguageSwitcherPopup
      v-if="popupChannel"
      :key
      :current-language="language"
      :alternatives="popupChannel.translations"
    />
  </div>
</template>

<script setup lang="ts">
import type { Channel } from '#graphql-operations'
import type { AvailableChannel } from '~/composables/useInitData/types'

defineProps<{
  channels: AvailableChannel[]
  activeChannel: Channel
}>()

const language = useCurrentLanguage()
const router = useRouter()
const { trackEvent } = useAnalytics()

const { activeModal } = useModal()

const activeToggleStyle: Record<string, string> = {
  'anchor-name': '--channel-anchor',
}

const popupChannel = ref<AvailableChannel | null>(null)
const key = ref(0)

function onClick(e: MouseEvent, channel: AvailableChannel) {
  trackEvent({
    event: 'click_context_channel',
    contextChannelName: channel.enum,
  })
  key.value++
  popupChannel.value = null
  e.preventDefault()
  trackEvent({
    event: 'click_context_channel',
    contextChannelName: channel.enum,
  })
  const hasTranslation = !!channel.translations[language.value]
  if (hasTranslation) {
    router.push(channel.landingPage)
    return
  }

  popupChannel.value = channel
}

onMounted(() => {
  if (!popupChannel.value && activeModal.value === 'language-popup') {
    activeModal.value = ''
  }
})
</script>

<style>
@reference '~/assets/css/tailwind.css';
.channels {
  a {
    @apply pb-0;
    line-height: 16px;
  }
  /* Fallback when anchor positioning is not supported. */
  a.is-active {
    @apply border-b-4 border-b-primary;
  }
}

@supports (position-anchor: inherit) {
  .channels {
    /* Animated underline on the active channel. */
    &:before {
      content: '';
      @apply pointer-events-none absolute bottom-0 left-0 h-[3px] bg-primary;
      @apply transition-all duration-300 ease-bounce;
      position-anchor: --channel-anchor;
      left: anchor(left);
      width: anchor-size(width);
    }

    /* Undo the fallback. */
    a.is-active {
      @apply !border-b-transparent;
    }
  }
}
</style>
